import { BackendErrorResponse } from "Backend";
import { AxiosError, isAxiosError } from "axios";
import { BulkCaseProfileExportMutationVariables } from "generated/graphql";
import Pendo from "helpers/Pendo";
import { enqueueSnackbar } from "notistack";
import { useAppDispatch } from "redux/hooks";
import {
  bulkCaseExport,
  createCaseChronologyExport,
  createCaseExport,
  createCaseFullRecordSetExport,
} from "redux/slices/case";

export interface ExportParams {
  configId?: number;
  password?: string;
  collectShared: boolean;
  pushToTransfer: boolean;
  customTransferFolder?: string;
}

export function useExportFunctions() {
  const dispatch = useAppDispatch();

  const handleError = (e: Error) => {
    if (isAxiosError(e)) {
      enqueueSnackbar(`Error with export -- ${(e as AxiosError<BackendErrorResponse>).response?.data.message}`, {
        variant: "error",
      });
    } else {
      enqueueSnackbar(`Error with export`, { variant: "error" });
    }
    throw e;
  };

  const doBulkCaseExport = async (req: BulkCaseProfileExportMutationVariables) => {
    await dispatch(bulkCaseExport(req))
      .unwrap()
      .then(() => enqueueSnackbar("Export generation request successful", { variant: "success" }))
      .catch((e) => {
        enqueueSnackbar(`Error with export - ${e.message}`, { variant: "error" });
      });
  };

  const doCaseExport = async (caseId: number, req: ExportParams) => {
    const result = await dispatch(
      createCaseExport({
        caseId,
        collectShared: req.collectShared,
        pushToTransfer: req.pushToTransfer,
        customTransferFolder: req.customTransferFolder,
        password: req.password,
        configId: req.configId,
      })
    )
      .unwrap()
      .catch(handleError);

    Pendo.event("case_form_export", {});

    return result;
  };

  const doChronologyExport = async (
    caseId: number,
    shareToCollect: boolean,
    pushToTransfer: boolean,
    customTransferFolder?: string
  ) => {
    const result = await dispatch(
      createCaseChronologyExport({
        caseId,
        collectShared: shareToCollect,
        pushToTransfer,
        customTransferFolder,
      })
    )
      .unwrap()
      .catch(handleError);

    return result;
  };

  const doFullRecordSetExport = async (caseId: number, req: ExportParams) => {
    const result = await dispatch(
      createCaseFullRecordSetExport({
        caseId,
        collectShared: req.collectShared,
        pushToTransfer: req.pushToTransfer,
        customTransferFolder: req.customTransferFolder,
        password: req.password,
        configId: req.configId,
      })
    )
      .unwrap()
      .catch(handleError);

    return result;
  };

  return {
    doBulkCaseExport,
    doCaseExport,
    doChronologyExport,
    doFullRecordSetExport,
  };
}
